import { Injectable } from 'injection-js';
import { Module, State } from 'shared/core';
import { Actions } from 'shared/state';
import 'webshop/elements/sizingselect';
import { Helpers } from 'shared/misc/helpers';
import { Flash } from 'shared/lib';

export interface FitStatement {
  name?: string;
  statement: string;
}

export interface FitStatementsParsed {
  yearCurrent: FitStatement[];
  yearNext: FitStatement[];
}

@Injectable()
/**
 * Handles the interactions on the sub_school_products screen in the WebShop.  Namely, the email requested modal and
 * handling size selection changes (fit statements, price, etc).
 */
export class ProductModule extends Module {
  private subSchoolItemId: JQuery = $('#shop_item_sub_school_item_id');
  private productSizing: JQuery = $('.product-sizing');
  private yearCurrent: JQuery = $('#yearCurrent');
  private yearNext: JQuery = $('#yearNext');
  private diagramHelpers: JQuery = $('#diagramHelpers');
  private currencySymbol: string = $('#product-price').data('currency');
  private canBackOrder: boolean = !!$('#product-price').data('canBackOrder');
  private forceRequest: boolean = !!$('#product-price').data('forceRequest');
  private quantity: JQuery = $('#shop_item_quantity');

  private sbSizeSelect: JQuery;
  private statements: FitStatementsParsed;

  private isPreview: boolean = $('.product.preview').length > 0;

  constructor(
    private state: State
  ) {
    super();
  }

  public init(): void {
    if (this.subSchoolItemId.length == 1 && this.subSchoolItemId.html()) {
      this.sbSizeSelect = (this.subSchoolItemId as any).sbsizing({
        maxHeight: 320,
        onSelected: () => this.onSelected(),
      });
    }

    $('#product-price').removeClass('hidden');

    $('.personalisation').each((i, el) => {
      let personalisationRow = $(el);
      let personalisationInput = personalisationRow.find('.personalisationSelectedValue');
    });

    $('body').on('click', '#requestEmailButton', e => {
      e.preventDefault();
      this.openEmailRequestForm();
    });
  }

  /**
   * Opens the Request Email model, filling in the sub_school_item_id field with the currently selected item.
   * @private
   */
  private openEmailRequestForm(): void {
    $('#confirm_email_request_form [name="sub_school_item_id"]').val(this.subSchoolItemId.val());
    this.state.dispatch({ name: Actions.OPEN_STATIC_MODAL, payload: 'confirmEmailModal' });
  }

  /**
   * Callback that is fired when sbSizeSelect size is changed. Updates the details on the screen to match those for the
   * current selected item.
   * @private
   */
  private onSelected(): void {
    const selectedOption = this.subSchoolItemId.find(':selected');
    const $emailRequested = $('.email_requested');
    const $addToBasket = $('.add_to_basket');

    // .email_requested shows when an email request flow is active AND this user has already
    // requested for the selected item. Active means: item is on back-order AND either the user
    // can't back-order OR the org forces the request prompt.

    const isBackorder = !!selectedOption.data('backorder');
    const showRequest = isBackorder && (!this.canBackOrder || this.forceRequest);
    const showAvailable = !isBackorder || this.canBackOrder;
    const emailRequested = !!selectedOption.data('emailRequested');
    $('.shop_available').toggle(showAvailable);
    $('.shop_request').toggle(showRequest && !emailRequested);
    $emailRequested.toggle(showRequest && emailRequested);

    // Update the quantity available drop-down box
    if (showAvailable) {
      this.changeAvailableQuantity(Number.parseInt(selectedOption.data('shopQuantity')));
    }

    if (selectedOption.data('inBasket')) {
      $addToBasket.prop('disabled', true);
      $addToBasket.prop('title', 'Already in basket');
      $addToBasket.html('Already in basket');
    } else {
      $addToBasket.prop('disabled', this.isPreview);
      $addToBasket.prop('title', '');
      $addToBasket.html('Add to Basket');
    }

    // Refresh Fit Statements
    this.changeFitStatements(selectedOption.data('currentYear'), selectedOption.data('nextYear'));

    // Set the "suggested field"
    $('input#suggested').val(selectedOption.data('suggested'));

    // Refresh Price
    this.changePrice(selectedOption.data('fullPrice'), selectedOption.data('promotionalPrice'));
  }

  /**
   * Change the Fit Statements on the page and updates the diagram helpers with the correct details.
   *
   * @param currentYear
   * @param nextYear
   * @private
   */
  private changeFitStatements(currentYear: FitStatement[], nextYear: FitStatement[]): void {
    this.statements = {
      yearCurrent: currentYear?.map(a => ({ statement: a[0], name: a[1] })) ?? [],
      yearNext: nextYear?.map(a => ({ statement: a[0], name: a[1] })) ?? [],
    };

    // Change the fit statements for the current and next year
    this.changeFitStatementsForYear(this.yearCurrent, this.statements.yearCurrent);
    this.changeFitStatementsForYear(this.yearNext, this.statements.yearNext);

    // Set the diagram helpers
    this.diagramHelpers.html('');
    this.statements.yearCurrent.forEach(data => {
      this.diagramHelpers.append(`<div class="measurements-diagram-silhouettes-${data.name}" />`);
    });

    // Only show the fit statements dialog if there are actually any fit statements to show
    if (this.statements.yearNext.length > 0 || this.statements.yearCurrent.length > 0) {
      this.productSizing.show();
    } else {
      this.productSizing.hide();
    }
  }

  /**
   * Change the Fit Statements for a specific year.
   *
   * @param property
   * @param yearStatement
   * @private
   */
  private changeFitStatementsForYear(property: JQuery, yearStatement: FitStatement[]): void {
    const sizeValues: JQuery = property.find('.sizingValues');
    sizeValues.html('');

    if (yearStatement.length == 0) {
      property.hide();
    } else {
      property.show();
    }

    yearStatement.forEach(data => {
      sizeValues.append(`<p>${data.statement}</p>`);
    });
  }

  /**
   * Changes the price to the specified prices.  If the full price and the promotional price are different then strike-
   * through pricing is displayed.  Otherwise, only the full price is displayed.
   *
   * @param fullPrice
   * @param promotionalPrice
   * @private
   */
  private changePrice(fullPrice: number, promotionalPrice: number): void {
    if (fullPrice === undefined) {
      $('.price-offer').hide();
      $('.price-current').hide();
    } else if (promotionalPrice != fullPrice) {
      $('.price-offer').show();
      $('.price-current').hide();

      $('.price-offer-old').html(Helpers.currencyFormatter(fullPrice, this.currencySymbol));
      $('.price-offer-new').html(Helpers.currencyFormatter(promotionalPrice, this.currencySymbol));
    } else {
      $('.price-offer').hide();
      $('.price-current').show();
      $('.price-full').html(Helpers.currencyFormatter(fullPrice, this.currencySymbol));
    }
  }

  /**
   * Change the available quantity for the item.
   * @param quantity
   * @private
   */
  private changeAvailableQuantity(quantity: number): void {
    const cached_quantity = this.quantity.val();

    this.quantity.empty();
    for(let i = 1; i <= quantity; i++){
      this.quantity.append(`<option value="${i}">${i}</option>`);
    }

    if (cached_quantity && cached_quantity != this.quantity.val()) {
      this.quantity.val(cached_quantity);
    }
  }

  public cleanup(): void {
    /* Since SbSizing has not destroy method manual cleanup needs to be done */
    $('.dd-container').remove();
  }
}
